import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Handshake, Search, ThumbsUp, RotateCcw, ExternalLink, FileDown, Clock,
  CalendarRange, CheckCircle2, Loader2, Send, Inbox, Copy, RefreshCw,
  ShieldCheck, MapPin, Monitor, MailCheck, MailWarning, AlertTriangle, ChevronDown,
  Pencil, UserRound, Store, Heart, TrendingUp, CalendarHeart, Bell,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatesPicker } from "@/components/DatesPicker";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const BRAND = "#EE722E";
const GREEN = "#1F9D55";

function frenchDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}

function frenchDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// Bloc « Preuve & authenticité » d'une confirmation : identité + IP + appareil +
// horodatage, et surtout le STATUT D'ENVOI des emails liés (pour lever le doute
// quand un « Échec envoi » laisse penser que le directeur n'a pas reçu le lien).
function ProofPanel({ ack }) {
  const [open, setOpen] = useState(false);
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !proof) {
      setLoading(true);
      try {
        const { data } = await api.get(`/ack/${ack.id}/proof`);
        setProof(data);
      } catch { /* silencieux */ } finally { setLoading(false); }
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-[rgba(31,157,85,0.25)] bg-[rgba(31,157,85,0.06)] p-2.5" data-testid={`nego-proof-${ack.id}`}>
      <div className="flex items-start gap-2 text-[11px] text-[rgba(24,24,24,0.72)]">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0F5A2F]" />
        <div className="min-w-0 flex-1">
          <div>
            <span className="font-semibold text-[#0F5A2F]">{ack.director_name || "Responsable"}</span>
            {ack.director_email ? ` · ${ack.director_email}` : ""}
            {ack.confirmed_at ? <span className="text-muted-ink"> · le {frenchDateTime(ack.confirmed_at)}</span> : ""}
          </div>
          {(ack.ip || ack.user_agent) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-muted-ink">
              {ack.ip && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />IP {ack.ip}</span>}
            </div>
          )}
        </div>
        <button onClick={toggle} data-testid={`nego-proof-toggle-${ack.id}`}
          className="inline-flex shrink-0 items-center gap-0.5 rounded-md border border-[rgba(31,157,85,0.35)] bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.08)]">
          Vérifier <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="mt-2 border-t border-[rgba(31,157,85,0.2)] pt-2" data-testid={`nego-proof-detail-${ack.id}`}>
          {loading ? (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Vérification…</div>
          ) : !proof ? (
            <div className="text-[11px] text-muted-ink">Impossible de charger la preuve.</div>
          ) : (
            <div className="space-y-2 text-[11px]">
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                <div className="rounded-md bg-white/70 px-2 py-1.5">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Appareil du clic</div>
                  <div className="mt-0.5 inline-flex items-center gap-1 font-semibold text-ink">
                    <Monitor className="h-3.5 w-3.5" />{proof.device?.label || "Inconnu"}
                    {proof.device?.device_type ? <span className="text-muted-ink">· {proof.device.device_type}</span> : ""}
                  </div>
                </div>
                <div className="rounded-md bg-white/70 px-2 py-1.5">
                  <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Adresse IP</div>
                  <div className="mt-0.5 font-semibold text-ink">{proof.ip || "Non enregistrée"}</div>
                </div>
              </div>

              {/* Statut d'envoi des emails liés */}
              <div>
                <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-muted-ink">Emails liés</div>
                {(proof.emails || []).length === 0 ? (
                  <div className="text-muted-ink">Aucun email journalisé pour cet accusé.</div>
                ) : (
                  <div className="space-y-1">
                    {proof.emails.map((e, i) => {
                      const failed = e.status === "failed";
                      return (
                        <div key={i} className="flex items-center justify-between gap-2 rounded-md bg-white/70 px-2 py-1" data-testid={`nego-proof-email-${ack.id}-${i}`}>
                          <span className="break-words text-ink">
                            <span className="font-semibold capitalize">{e.type || "email"}</span>
                            <span className="text-muted-ink"> → {e.to}</span>
                          </span>
                          <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${failed ? "bg-[rgba(192,57,43,0.12)] text-[#8F2A20]" : "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]"}`}>
                            {failed ? <><MailWarning className="h-3 w-3" />Échec envoi</> : <><MailCheck className="h-3 w-3" />Envoyé</>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {proof.delivery?.warning && (
                <div className="flex items-start gap-1.5 rounded-md border border-[rgba(192,57,43,0.3)] bg-[rgba(192,57,43,0.06)] px-2 py-1.5 text-[10.5px] font-medium text-[#8F2A20]" data-testid={`nego-proof-warning-${ack.id}`}>
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{proof.delivery.warning}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_META = {
  countered: { label: "Contre-proposition", cls: "bg-[rgba(238,114,46,0.14)] text-[#8A3A10]", Icon: Handshake },
  proposed: { label: "En attente magasin", cls: "bg-[rgba(233,184,0,0.16)] text-[#5A4200]", Icon: CalendarRange },
  pending: { label: "Confirmation en attente", cls: "bg-[rgba(233,184,0,0.16)] text-[#5A4200]", Icon: Clock },
  confirmed: { label: "Confirmé", cls: "bg-[rgba(31,157,85,0.12)] text-[#0F5A2F]", Icon: CheckCircle2 },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`} data-testid={`nego-badge-${status}`}>
      <m.Icon className="h-3.5 w-3.5" /> {m.label}
    </span>
  );
}

function AckCard({ ack, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [reproposing, setReproposing] = useState(false);
  const [rpDates, setRpDates] = useState("");
  const [rpIso, setRpIso] = useState([]);
  const [rpNote, setRpNote] = useState("");
  // Rattrapage « Au crayon » (anciens e-mails de confirmation)
  const [placing, setPlacing] = useState(false);
  const [pencilMode, setPencilMode] = useState(false);
  const [plDates, setPlDates] = useState("");
  const [plIso, setPlIso] = useState([]);

  const isProp = (ack.kind || "confirmation") === "proposition";
  const publicUrl = `${window.location.origin}/confirmation/${ack.token}`;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/ack/${ack.id}/approve`);
      toast.success("Dates approuvées — positionnées en « Ferme » sur le planning");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'approbation");
    } finally { setBusy(false); }
  };

  const submitRepropose = async () => {
    if (!rpDates.trim() && rpIso.length === 0) { toast.error("Choisissez au moins une date"); return; }
    setBusy(true);
    try {
      await api.post(`/ack/${ack.id}/repropose`, {
        dates: rpDates.trim() || undefined,
        iso_dates: rpIso.length ? rpIso : undefined,
        note: rpNote.trim() || undefined,
      });
      toast.success("Nouvelle proposition envoyée au directeur");
      setReproposing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la reproposition");
    } finally { setBusy(false); }
  };

  const copyLink = () => { navigator.clipboard?.writeText(publicUrl); toast.success("Lien copié"); };

  // Peut-on placer en 1 clic (l'accusé porte déjà des dates) ?
  const hasDates = (ack.iso_dates && ack.iso_dates.length) || (ack.dates && String(ack.dates).trim());

  const placePencil = async (iso) => {
    setPlacing(true);
    try {
      const body = iso && iso.length ? { iso_dates: iso } : {};
      const { data } = await api.post(`/ack/${ack.id}/place-pencil`, body);
      toast.success(`${data.placed} créneau(x) placé(s) « Au crayon » sur le planning`);
      setPencilMode(false);
      onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Placement impossible";
      // Dates manquantes côté accusé → on ouvre le sélecteur de dates.
      if (/date/i.test(detail) && !(iso && iso.length)) setPencilMode(true);
      toast.error(detail);
    } finally { setPlacing(false); }
  };

  const onPlaceClick = () => { if (hasDates) placePencil(); else setPencilMode(true); };

  return (
    <div className="rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid={`nego-card-${ack.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink">
              {isProp ? "Proposition" : "Confirmation"}
            </span>
            <h3 className="break-words text-sm font-bold text-ink" data-testid={`nego-spot-${ack.id}`}>{ack.spot_name || "Spot ?"}</h3>
          </div>
          <div className="mt-0.5 text-xs text-muted-ink">
            {[ack.ville, ack.association_acronym || ack.association_name].filter(Boolean).join(" · ")}
          </div>
        </div>
        <StatusBadge status={ack.status} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {ack.dates && (
          <span className="inline-flex items-center gap-1 text-ink">
            <CalendarRange className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
            <span className="font-semibold">{ack.dates}</span>
          </span>
        )}
        <span className="text-muted-ink">Créé le {frenchDate(ack.created_at)}{ack.cdp_name ? ` · ${ack.cdp_name}` : ""}</span>
      </div>

      {/* Contre-proposition du magasin */}
      {ack.status === "countered" && (
        <div className="mt-3 rounded-xl border border-[rgba(238,114,46,0.28)] bg-[#FFF7F1] px-3 py-2" data-testid={`nego-counter-${ack.id}`}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[#8A3A10]">Le magasin propose</div>
          <div className="mt-0.5 text-[14px] font-bold text-ink">{ack.counter_dates || "Dates transmises"}</div>
          {ack.counter_note && <div className="mt-1 text-[12px] italic text-muted-ink">« {ack.counter_note} »</div>}

          {reproposing ? (
            <div className="mt-3 space-y-2" data-testid={`nego-repropose-form-${ack.id}`}>
              <DatesPicker value={rpDates} onChange={setRpDates} onDatesChange={setRpIso}
                label={null} testId={`nego-rp-dates-${ack.id}`} helperText="Nouvelles dates à proposer au directeur." />
              <Textarea value={rpNote} onChange={(e) => setRpNote(e.target.value)} rows={2}
                placeholder="Message (facultatif)…" className="bg-white text-sm" data-testid={`nego-rp-note-${ack.id}`} />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitRepropose} disabled={busy}
                  className="flex-1 text-white" style={{ background: BRAND }} data-testid={`nego-rp-send-${ack.id}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Envoyer</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setReproposing(false)} disabled={busy}
                  className="border-[var(--border-soft)] bg-white" data-testid={`nego-rp-cancel-${ack.id}`}>Annuler</Button>
              </div>
            </div>
          ) : (
            <div className="mt-2 flex gap-2">
              <Button size="sm" onClick={approve} disabled={busy}
                className="flex-1 text-white" style={{ background: GREEN }} data-testid={`nego-approve-${ack.id}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ThumbsUp className="mr-1.5 h-4 w-4" /> Approuver</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReproposing(true)} disabled={busy}
                className="flex-1 border-[rgba(238,114,46,0.4)] bg-white text-[#8A3A10] hover:bg-[#FFF3E9]" data-testid={`nego-repropose-${ack.id}`}>
                <RotateCcw className="mr-1.5 h-4 w-4" /> Reproposer
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Confirmé : preuve d'authenticité + attestation */}
      {ack.status === "confirmed" && <ProofPanel ack={ack} />}

      {/* Rattrapage : sélection des dates si l'accusé n'en porte pas */}
      {pencilMode && (
        <div className="mt-3 rounded-xl border border-[rgba(233,184,0,0.45)] bg-[#FFFDF5] px-3 py-2.5" data-testid={`nego-pencil-form-${ack.id}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#5A4200]">
            <Pencil className="h-3 w-3" /> Dates à placer au crayon
          </div>
          <div className="mt-2">
            <DatesPicker value={plDates} onChange={setPlDates} onDatesChange={setPlIso}
              label={null} testId={`nego-pencil-dates-${ack.id}`}
              helperText="Sélectionnez les jours convenus dans l'e-mail de confirmation." />
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={() => placePencil(plIso)} disabled={placing || plIso.length === 0}
              className="flex-1 font-semibold text-[#3F2E00]" style={{ background: "#E9B800" }}
              data-testid={`nego-pencil-submit-${ack.id}`}>
              {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Pencil className="mr-1.5 h-4 w-4" /> Placer au crayon</>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setPencilMode(false)} disabled={placing}
              className="border-[var(--border-soft)] bg-white" data-testid={`nego-pencil-cancel-${ack.id}`}>Annuler</Button>
          </div>
        </div>
      )}

      {/* Actions transverses */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[var(--border-soft)] pt-2.5">
        <a href={publicUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]"
          data-testid={`nego-open-${ack.id}`}>
          <ExternalLink className="h-3.5 w-3.5" /> Ouvrir la page
        </a>
        <button onClick={copyLink}
          className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]"
          data-testid={`nego-copy-${ack.id}`}>
          <Copy className="h-3.5 w-3.5" /> Copier le lien
        </button>
        {!isProp && ack.status !== "confirmed" && !pencilMode && (
          <button onClick={onPlaceClick} disabled={placing}
            className="inline-flex items-center gap-1 rounded-md border border-[rgba(233,184,0,0.55)] bg-[#FFFDF5] px-2 py-1 text-[11px] font-semibold text-[#5A4200] transition-colors hover:bg-[#FFF7DB] disabled:opacity-50"
            data-testid={`nego-place-pencil-${ack.id}`}
            title="Poser les créneaux demandés « Au crayon » sur le planning (sans renvoyer l'e-mail)">
            {placing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />} Placer au crayon
          </button>
        )}
        {ack.status === "confirmed" && ack.token && (
          <a href={`${API}/ack/public/${ack.token}/attestation.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-[rgba(31,157,85,0.4)] bg-white px-2 py-1 text-[11px] font-semibold text-[#0F5A2F] transition-colors hover:bg-[rgba(31,157,85,0.08)]"
            data-testid={`nego-attestation-${ack.id}`}>
            <FileDown className="h-3.5 w-3.5" /> Attestation PDF
          </a>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "countered", label: "À traiter" },
  { key: "proposed", label: "En attente magasin" },
  { key: "pencil", label: "Au crayon" },
  { key: "confirmed", label: "Confirmés" },
  { key: "tous", label: "Tous" },
];

// Formate une liste de dates ISO en libellé court FR (« 3, 4 et 5 août »).
function frenchDateList(isoList) {
  if (!isoList || !isoList.length) return "";
  try {
    return isoList
      .map((d) => new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }))
      .join(", ");
  } catch {
    return isoList.join(", ");
  }
}

// Carte « Au crayon » : un magasin dont les créneaux sont posés au planning mais
// PAS encore confirmés par le directeur. On propose le lien de relance s'il existe.
function PencilCard({ item }) {
  const publicUrl = item.ack_token ? `${window.location.origin}/confirmation/${item.ack_token}` : null;
  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Lien de confirmation copié");
  };
  return (
    <div className="rounded-2xl border border-[rgba(233,184,0,0.4)] bg-[#FFFDF5] p-4 shadow-[var(--shadow-sm)]" data-testid={`pencil-card-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(233,184,0,0.18)] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#5A4200]">
              <Pencil className="h-3 w-3" /> Au crayon
            </span>
            <h3 className="break-words text-sm font-bold text-ink" data-testid={`pencil-spot-${item.id}`}>{item.spot_name}</h3>
          </div>
          <div className="mt-0.5 text-xs text-muted-ink">
            {[item.ville, item.association_acronym].filter(Boolean).join(" · ")}
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(233,184,0,0.16)] px-2 py-0.5 text-[11px] font-semibold text-[#5A4200]">
          <Clock className="h-3.5 w-3.5" /> Directeur non confirmé
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1 text-ink">
          <CalendarRange className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          <span className="font-semibold">{frenchDateList(item.dates)}</span>
          <span className="text-muted-ink">· {item.count} jour(s)</span>
        </span>
        {item.ligne_identifiant && (
          <span className="inline-flex items-center gap-1 text-muted-ink"><Store className="h-3.5 w-3.5" />{item.ligne_identifiant}</span>
        )}
        {item.cdp_name && (
          <span className="inline-flex items-center gap-1 text-muted-ink"><UserRound className="h-3.5 w-3.5" />{item.cdp_name}</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[rgba(233,184,0,0.3)] pt-2.5">
        {publicUrl ? (
          <>
            <a href={publicUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]"
              data-testid={`pencil-open-${item.id}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Ouvrir la page de confirmation
            </a>
            <button onClick={copyLink}
              className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]"
              data-testid={`pencil-copy-${item.id}`}>
              <Copy className="h-3.5 w-3.5" /> Copier le lien de relance
            </button>
          </>
        ) : (
          <span className="text-[11px] italic text-muted-ink" data-testid={`pencil-nolink-${item.id}`}>
            Pas encore de lien de confirmation envoyé — préparez-le depuis la fiche du spot ou la page Emails.
          </span>
        )}
      </div>
    </div>
  );
}

// Widget « Statistiques de renouvellement » : mesure le ROI des mails de
// remerciement (bouton « choisir vos prochaines dates »). Combien de magasins
// ont proposé de nouvelles dates après avoir été remerciés.
function RenewalStats({ data }) {
  if (!data || !data.sent) {
    return (
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-dashed border-[var(--border-soft)] bg-[#FFF7F1] px-4 py-3.5" data-testid="renewal-stats-empty">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-[var(--shariti-orange)]"><CalendarHeart className="h-5 w-5" /></span>
        <div>
          <div className="text-sm font-bold text-[#8A3A10]">Statistiques de renouvellement</div>
          <div className="text-[12px] text-muted-ink">
            Aucun remerciement avec proposition de dates envoyé pour l'instant. Utilisez « Remercier le magasin » (fiche spot ou planning) pour démarrer.
          </div>
        </div>
      </div>
    );
  }
  const { sent = 0, responded = 0, booked = 0, pending = 0, rate = 0, booked_rate = 0 } = data;
  const metrics = [
    { key: "sent", label: "Remerciements envoyés", value: sent, hint: "avec proposition de dates", Icon: Heart, color: "#8A3A10", bg: "rgba(238,114,46,0.12)" },
    { key: "responded", label: "Ont proposé des dates", value: responded, hint: `${rate}% de réponse`, Icon: CalendarHeart, color: "#0F5A2F", bg: "rgba(31,157,85,0.12)" },
    { key: "booked", label: "Ont re-réservé", value: booked, hint: `${booked_rate}% confirmés`, Icon: CheckCircle2, color: "#0F5A2F", bg: "rgba(31,157,85,0.10)" },
    { key: "pending", label: "En attente de réponse", value: pending, hint: "magasin(s) sollicité(s)", Icon: Clock, color: "#5A4200", bg: "rgba(233,184,0,0.16)" },
  ];
  return (
    <div className="mb-5 rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="renewal-stats">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)]"><TrendingUp className="h-4 w-4" /></span>
        <div>
          <div className="text-sm font-bold text-ink">Statistiques de renouvellement</div>
          <div className="text-[11px] text-muted-ink">Impact des mails de remerciement — magasins qui reprennent contact pour de prochaines dates.</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {metrics.map((m) => (
          <div key={m.key} className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-1)] p-3" data-testid={`renewal-metric-${m.key}`}>
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: m.bg, color: m.color }}><m.Icon className="h-4 w-4" /></span>
              <span className="text-2xl font-extrabold tabular-nums text-ink" data-testid={`renewal-value-${m.key}`}>{m.value}</span>
            </div>
            <div className="mt-1.5 text-[12px] font-semibold text-ink">{m.label}</div>
            <div className="text-[11px] text-muted-ink">{m.hint}</div>
          </div>
        ))}
      </div>
      {/* Barre de conversion (réponse) */}
      <div className="mt-3">
        <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-muted-ink">
          <span>Taux de renouvellement</span>
          <span className="text-[#0F5A2F]" data-testid="renewal-rate-label">{rate}%</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]" role="progressbar" aria-valuenow={rate} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-[#1F9D55] transition-[width] duration-500" style={{ width: `${Math.min(rate, 100)}%` }} data-testid="renewal-rate-bar" />
        </div>
      </div>
    </div>
  );
}

// Liste des renouvellements EN ATTENTE : magasins remerciés qui n'ont pas encore
// choisi de dates. Le CDP peut leur envoyer, à la main, une relance douce.
function RenewalPendingList({ items, relancingId, onRelance }) {
  if (!items || !items.length) return null;
  return (
    <div className="mb-5 rounded-2xl border border-[var(--border-soft)] bg-white p-4 shadow-[var(--shadow-sm)]" data-testid="renewal-pending">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-[rgba(233,184,0,0.18)] text-[#5A4200]"><Bell className="h-4 w-4" /></span>
        <div>
          <div className="text-sm font-bold text-ink">Relance douce — en attente de dates</div>
          <div className="text-[11px] text-muted-ink">Magasins remerciés qui n'ont pas encore choisi leurs prochaines dates. Envoyez, à la main, un petit mot bienveillant (sans engagement).</div>
        </div>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((it) => {
          const busy = relancingId === it.id;
          const canRelance = it.has_email;
          return (
            <div key={it.id} className="flex items-start justify-between gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-1)] p-3" data-testid={`renewal-pending-${it.id}`}>
              <div className="min-w-0">
                <div className="break-words text-sm font-bold text-ink" data-testid={`renewal-pending-spot-${it.id}`}>{it.spot_name}</div>
                <div className="mt-0.5 break-words text-[11px] text-muted-ink">
                  {[it.ville, it.association_acronym || it.association_name].filter(Boolean).join(" · ") || "—"}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
                  {it.cdp_name && <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{it.cdp_name}</span>}
                  {it.relance_count > 0 && (
                    <span className="inline-flex items-center gap-1 font-semibold text-[#5A4200]" data-testid={`renewal-relance-count-${it.id}`}>
                      <Bell className="h-3 w-3" />{it.relance_count} relance{it.relance_count > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                {!canRelance && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-[#9B2226]">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> Pas d'email responsable — impossible de relancer
                  </div>
                )}
              </div>
              <Button size="sm" variant="outline" disabled={busy || !canRelance} onClick={() => onRelance(it)}
                className="shrink-0 gap-1 border-[var(--border-soft)] bg-white" data-testid={`renewal-relance-btn-${it.id}`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {it.relance_count > 0 ? "Relancer encore" : "Relancer"}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Negotiations() {
  const [items, setItems] = useState([]);
  const [pencil, setPencil] = useState([]);
  const [renewal, setRenewal] = useState(null);
  const [renewalsPending, setRenewalsPending] = useState([]);
  const [relancingId, setRelancingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("countered");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acks, pen, ren, rp] = await Promise.all([
        api.get("/ack"),
        api.get("/ack/pencil").catch(() => ({ data: [] })),
        api.get("/ack/renewal-stats").catch(() => ({ data: null })),
        api.get("/ack/renewals-pending").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(acks.data) ? acks.data : []);
      setPencil(Array.isArray(pen.data) ? pen.data : []);
      setRenewal(ren.data || null);
      setRenewalsPending(Array.isArray(rp.data) ? rp.data : []);
      // Rafraîchit la pastille du menu (après approbation / reproposition notamment).
      window.dispatchEvent(new Event("shariti:acks-updated"));
    } catch {
      setItems([]);
      setPencil([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Relance douce MANUELLE : renvoie au magasin un petit mot bienveillant + le bouton
  // « choisir vos prochaines dates » (brandé association). Ne confirme rien : le
  // magasin reste en attente, on incrémente juste le compteur de relances.
  const doRelance = useCallback(async (it) => {
    setRelancingId(it.id);
    try {
      const { data } = await api.post(`/ack/${it.id}/relance`);
      setRenewalsPending((prev) => prev.map((x) => x.id === it.id
        ? { ...x, relance_count: data.relance_count, last_relance_at: data.last_relance_at } : x));
      toast.success(`Petit mot bienveillant envoyé à ${data.to} 💛`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "La relance n'a pas pu être envoyée");
    } finally {
      setRelancingId("");
    }
  }, []);

  const counts = useMemo(() => ({
    countered: items.filter((a) => a.status === "countered").length,
    proposed: items.filter((a) => a.status === "proposed").length,
    pencil: pencil.length,
    confirmed: items.filter((a) => a.status === "confirmed").length,
    tous: items.length,
  }), [items, pencil]);

  const filteredPencil = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pencil;
    return pencil.filter((p) => [p.spot_name, p.ville, p.association_acronym, p.ligne_identifiant, p.cdp_name]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [pencil, q]);

  const filtered = useMemo(() => {
    let list = items;
    if (tab !== "tous") list = list.filter((a) => a.status === tab);
    const s = q.trim().toLowerCase();
    if (s) {
      list = list.filter((a) => [a.spot_name, a.ville, a.association_name, a.association_acronym,
        a.director_name, a.director_email, a.dates, a.counter_dates]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    }
    return list;
  }, [items, tab, q]);

  return (
    <>
      <PageHeader
        title="Négociations"
        subtitle="Suivi des propositions de dates et accusés de réception — les actions en attente sont regroupées ici."
        action={
          <Button variant="outline" onClick={load} disabled={loading}
            className="border-[var(--border-soft)] bg-white" data-testid="nego-refresh">
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
          </Button>
        }
      >
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (magasin, ville, association, directeur…)"
            className="bg-white pl-9" data-testid="nego-search" />
        </div>
      </PageHeader>

      <PageBody>
        {/* Statistiques de renouvellement (ROI des mails de remerciement) */}
        <RenewalStats data={renewal} />

        {/* Relance douce manuelle : magasins remerciés en attente de dates */}
        <RenewalPendingList items={renewalsPending} relancingId={relancingId} onRelance={doRelance} />

        {/* Onglets / filtres */}
        <div className="mb-5 flex flex-wrap gap-2" data-testid="nego-tabs">
          {TABS.map((t) => {
            const active = tab === t.key;
            const n = counts[t.key] || 0;
            const urgent = t.key === "countered" && n > 0;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                data-testid={`nego-tab-${t.key}`}
                className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  active
                    ? "border-transparent bg-[var(--shariti-orange)] text-white"
                    : "border-[var(--border-soft)] bg-white text-[rgba(24,24,24,0.7)] hover:bg-[var(--surface-2)]"
                }`}>
                {t.label}
                <span className={`grid min-w-[20px] place-items-center rounded-full px-1.5 text-[11px] font-bold ${
                  active ? "bg-white/25 text-white"
                    : urgent ? "bg-[#D64545] text-white" : "bg-[var(--surface-2)] text-muted-ink"
                }`}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
          </div>
        ) : tab === "pencil" ? (
          filteredPencil.length === 0 ? (
            <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="pencil-empty">
              <Pencil className="h-10 w-10 text-muted-ink" />
              <h3 className="mt-3 text-base font-bold text-ink">Aucun magasin au crayon</h3>
              <p className="mt-1 max-w-sm text-sm text-muted-ink">
                Tous les créneaux posés au planning ont été confirmés par les directeurs, ou aucun n'est en attente.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="pencil-list">
              {filteredPencil.map((p) => <PencilCard key={p.id} item={p} />)}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-white py-16 text-center" data-testid="nego-empty">
            <Inbox className="h-10 w-10 text-muted-ink" />
            <h3 className="mt-3 text-base font-bold text-ink">
              {tab === "countered" ? "Aucune action en attente" : "Rien à afficher"}
            </h3>
            <p className="mt-1 max-w-sm text-sm text-muted-ink">
              {tab === "countered"
                ? "Aucune contre-proposition à traiter pour le moment. Vous êtes à jour !"
                : "Aucun élément ne correspond à ce filtre."}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="nego-list">
            {filtered.map((a) => <AckCard key={a.id} ack={a} onChanged={load} />)}
          </div>
        )}
      </PageBody>
    </>
  );
}
